// noinspection ExceptionCaughtLocallyJS,JSUnusedAssignment

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  ApiRequestsFilters,
  changeResponse,
  getRequests,
  getOpenResponses,
} from '../../../services/requestEndPoints';
import {
  IRequestsPayloadState,
  RequestData,
  RequestsInterface,
} from 'interfaces/request.interface';
import { ICreateResponse, IResponsesDB } from 'interfaces/response.interface';
import apiFetch from 'services/apiFetch';
import { ResponseStatus } from '../../../common/enums';

export const fetchRequests = createAsyncThunk(
  'dashboard/fetchRequests',
  async (filters: ApiRequestsFilters) => {
    try {
      const endpoint = getRequests(filters);
      const response = await apiFetch<RequestsInterface>(endpoint).then((res) => res?.data || null);

      if (response) {
        const requestsData: RequestData[] = response.data.map((item) => {
          return {
            id: item.id,
            deleted: item.deleted,
            archived: item.archived,
            requestNumber: item.requestNumber,
            missionType: item.missionType,
            date: item.date,
            palmettoResourceRequest: item.palmettoResourceRequest,
            onSceneContactPerson: item.onSceneContactPerson,
            onSceneContactPhone: item.onSceneContactPhone,
            departmentName: item.departmentName,
            contactPerson: item.contactPerson,
            contactPhone: item.contactPhone,
            stagingLocation: item.stagingLocation,
            destination: item.destination,
            notes: item.notes,
            ambulance: item.ambulance,
            personnel: item.personnel,
            counties: item.counties,
            serviceType: item.serviceType,
            agencies: item.agencies,
            emailBody: item.emailBody,
            createdAt: item.createdAt,
            updatedAt: item.updatedAt,
            incident: item.incident,
            responses: item.responses,
          };
        });

        const page = response.page;
        const lastPage = response.last_page;
        const pagesTotal = response.total;

        const payload: IRequestsPayloadState = {
          requests: requestsData,
          page: page,
          lastPage: lastPage,
          pagesTotal: pagesTotal,
        };

        return payload;
      } else {
        throw 'No data found';
      }
    } catch (error) {
      let message = 'Something went wrong!';
      if (error instanceof Error) {
        message = error.message;
      } else message = String(error);
      console.log('fetchRequests catch:', error);
      throw Error(message);
    }
  },
);

export const fetchResponses = createAsyncThunk('dashboard/fetchResponses', async () => {
  try {
    return await apiFetch<IResponsesDB[]>(getOpenResponses())
      .then((res) => res?.data || [])
      .catch((e) => {
        throw e;
      });
  } catch (error) {
    let message = 'Something went wrong!';
    if (error instanceof Error) message = error.message;
    else message = String(error);
    console.log('fetchResponses catch:', error);
    throw Error(message);
  }
});

export const updateResponse = createAsyncThunk(
  'dashboard/update-response',
  async (data: { response: IResponsesDB; status: ResponseStatus }) => {
    try {
      // Before creating the payload, update ambulance items if status is ACCEPTED (1)
      const updatedAmbulance = data.response.requests.ambulance || [];
      if (data.status === 1) {
        updatedAmbulance.forEach((ambulance) => {
          if (ambulance.items) {
            ambulance.items.forEach((item) => {
              item.accepted = true;
            });
          }
        });
      }

      const payload: ICreateResponse = {
        requestId: data.response.requests.id,
        agencyId: data.response.agency.id || '',
        responseId: data.response.id,
        ambulance: updatedAmbulance,
        personnel: data.response.requests.personnel || [],
        status: data.status,
        declineMessage: data.response.declineMessage,
        primaryContactPerson: data.response.primaryContactPerson,
        primaryContactPhone: data.response.primaryContactPhone,
        primaryContactNotes: data.response.primaryContactNotes,
      };

      const params = { authorize: true };
      const endpoint = changeResponse(payload, params);
      const resp = await apiFetch<{ data: string }>(endpoint).then((res) => res?.data || null);

      if (resp && resp.data === 'Success') return resp.data;
      else throw 'Unable to update response';
    } catch (error) {
      let message = 'Something went wrong!';
      if (error instanceof Error) message = error.message;
      else {
        if (typeof error === 'object' && error !== null) {
          if ('message' in error) {
            message = String(error.message);
          }
        }
      }
      console.log('update response catch:', message);
      throw Error(message);
    }
  },
);
